import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Icon } from '@vds/icons';
import { ButtonIcon } from '@vds/button-icons';
import { Carousel } from '@vds/carousels';
import { postVzgptChat, retrievePastEvents } from '../utility/common';
import { parseSSEChunk } from '../utility/utils';
import '../components/common/inputSearch.scss';
import './OneSearch.scss';
import oneSearchHardCodeData from './oneSearchHardCodeData.json';

// ── Inline source card used inside the Sources accordion ──
const SourceCard = ({ format, title, imageUrl, link }) => {
    const isCourseDetail = link && link.includes('course-details');
    return (
        <a
            className="os-source-card"
            href={link}
            rel="noopener noreferrer"
        >
            <div className="os-source-card__img">
                <img src={imageUrl} alt={title} />
            </div>
            <div className="os-source-card__info">
                {isCourseDetail && (
                    <span className="vds-micro font-eTX-bold">{format}</span>
                )}
                <span className="vds-title-2xs os-source-card__title">{title}</span>
            </div>
        </a>
    );
};

// ── Recommended course card for the carousel ──
const RecommendedCourseCard = ({ title, snippet, link, format, duration, imageUrl, icon, type }) => (
    <a
        className="os-reco-card"
        href={link || '#'}
        rel="noopener noreferrer"
    >
        <div className="os-reco-card__img-wrap">
            <img src={imageUrl} alt={title} />
            <span className="os-reco-card__badge vds-body-sm font-eTX-bold">Recommended</span>
        </div>
        <div className="os-reco-card__body">
            <div className="os-reco-card__meta">
                <div className="os-reco-card__tags">
                    <span className="vds-micro font-eTX-bold">{format || ''}</span>
                    <span className="os-reco-card__dot" />
                    <span className="vds-micro">{type|| ''}</span>
                </div>
                <div className="vds-title-sm mobile-vds-title-xs os-reco-card__name">{title}</div>
            </div>
            <div className="os-reco-card__footer">
                <div className="os-reco-card__duration">
                    <Icon
                      name={icon || 'timer'}
                      size="small"
                      color={'#716F6D'}
                    />
                    <span className="vds-micro font-eTX-bold color-secondary">{duration}</span>
                </div>
                <Icon name="right-arrow" size='large' />
            </div>
        </div>
    </a>
);

export default function OneSearch() {
    const userData = useSelector((state) => state.data);
    const allCourses = useSelector((state) => state.AdobeResources.allCourses) ?? [];
    const allEventsWithTimeZone = useSelector((state) => state.AdobeResources.allEventsWithTimeZone) ?? [];
    const combinedQuickTips = useSelector((state) => state.Aem?.quickTipData?.combinedQuickTips) ?? [];
    const offerData = useSelector((state) => state.Aem?.offerData);
    const allOffers = offerData?.offers?.[0] ? Object.values(offerData.offers[0]).filter((item) => item.format === 'Offer') : [];
    const [allPastEvents, setAllPastEvents] = useState(null) ?? null;
    // const aiCommonData = useSelector((state) => state.Aem?.aiCommonData) ?? [];
    const [searchQuery, setSearchQuery] = useState('');
    const [searchHistory, setSearchHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const searchPlaceholder = t('What can we help you learn?');
    const historyRef = useRef(null);
    const abortControllerRef = useRef(null);
    const sourcesButtonRefs = useRef({});
    const summaryRefs = useRef({});
    const askAiData = useSelector((state) => state.Aem.aemAiSearchData);

    useEffect(() => {
        retrievePastEvents().then(pastEvents => {
            const data = pastEvents.data;
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                const pastEventsArray = Object.entries(data).map(([key, val]) => ({
                    ...val
                }));
                setAllPastEvents(pastEventsArray);
            } else {
                setAllPastEvents(data);
            }
        })
    }, []);
    // Auto-scroll to latest entry
    useEffect(() => {
        if (searchHistory.length > 0 && historyRef.current) {
            const container = historyRef.current;
            const entries = container.querySelectorAll('.os-entry');
            const lastEntry = entries[entries.length - 1];
            if (lastEntry) {
                container.scrollTo({ top: lastEntry.offsetTop - container.offsetTop, behavior: 'smooth' });
            }
        }
    }, [searchHistory]);

    const getGptSessionId = () => {
        let sessionId = sessionStorage.getItem('gptSessionID');
        if (!sessionId) {
            sessionId = crypto.randomUUID();
            sessionStorage.setItem('gptSessionID', sessionId);
        }
        return sessionId;
    };

    const handleSearch = useCallback(async (overrideQuery) => {
        const trimmed = (overrideQuery ?? searchQuery)?.trim();
        if (!trimmed || isLoading) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const entryIndex = searchHistory.length;
        setSearchHistory((prev) => [
            ...prev,
            {
                query: trimmed,
                streamingText: '',
                finalText: '',
                isStreaming: true,
                followUpQuestions: [],
                citations: [],
                searchResults: [],
                courseResults: [],
                sourcesOpen: false,
                error: null,
            },
        ]);
        setSearchQuery('');
        setIsLoading(true);
        // Focus the summary of the new entry once it mounts
        setTimeout(() => {
            const summary = summaryRefs.current[entryIndex];
            if (summary) summary.focus();
        }, 100);

        try {
            const response = await postVzgptChat({
                userId: 'vbguat-3931345767334233186080764982397701946543',
                sessionId: getGptSessionId(),
                message: trimmed,
                signal: controller.signal,
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = '';
            let finalText = '';
            let allFollowUps = [];
            let allCitations = [];
            let allSearchResults = [];
            let allCourseResults = [];
            let buffer = '';

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine.startsWith('data:')) continue;

                    const jsonStr = trimmedLine.slice(5).trim();
                    if (!jsonStr) continue;

                    const { text, isComplete, followUpQuestions, citations, searchResults, courseResults } = parseSSEChunk(jsonStr);
                    if (followUpQuestions.length > 0) {
                        allFollowUps = followUpQuestions;
                    }
                    if (citations.length > 0) {
                        allCitations = citations;
                    }
                    if (searchResults.length > 0) {
                        allSearchResults = searchResults;
                    }
                    if (courseResults.length > 0) {
                        allCourseResults = courseResults;
                    }

                    if (isComplete && text) {
                        finalText = text;
                    } else if (text) {
                        accumulatedText += text;
                    }

                    setSearchHistory((prev) => {
                        const updated = [...prev];
                        updated[entryIndex] = {
                            ...updated[entryIndex],
                            streamingText: finalText || accumulatedText,
                            followUpQuestions: allFollowUps,
                            citations: allCitations,
                            searchResults: allSearchResults,
                            courseResults: allCourseResults,
                        };
                        return updated;
                    });
                }
            }

            allCitations.forEach((c, i) => {
            });

            allSearchResults
                .filter((r) => r.relevance_score >= 0.4 && !/^(test|copy of)/i.test(r.title))
                .sort((a, b) => b.relevance_score - a.relevance_score)
                .forEach((r, i) => {
                });

            // Finalize entry
            setSearchHistory((prev) => {
                const updated = [...prev];
                updated[entryIndex] = {
                    ...updated[entryIndex],
                    finalText: finalText || accumulatedText,
                    streamingText: '',
                    isStreaming: false,
                    followUpQuestions: allFollowUps,
                    citations: allCitations,
                    searchResults: allSearchResults,
                    courseResults: allCourseResults,
                };
                return updated;
            });
        } catch (error) {
            if (error.name !== 'AbortError') {
                setSearchHistory((prev) => {
                    const updated = [...prev];
                    updated[entryIndex] = {
                        ...updated[entryIndex],
                        finalText: '',
                        streamingText: '',
                        isStreaming: false,
                        error: error.message || 'Something went wrong. Please try again.',
                    };
                    return updated;
                });
            }
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;
        }
    }, [searchQuery, isLoading, searchHistory.length, userData]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Toggle sources accordion for a specific entry
    const toggleSources = (historyIndex) => {
        setSearchHistory((prev) => {
            const updated = [...prev];
            updated[historyIndex] = {
                ...updated[historyIndex],
                sourcesOpen: !updated[historyIndex].sourcesOpen,
            };
            return updated;
        });
        // Restore focus to the sources toggle button after re-render
        setTimeout(() => {
            const btn = sourcesButtonRefs.current[historyIndex];
            if (btn) btn.focus();
        }, 0);
    };

    // Parse markdown-like response text into highlighted HTML blocks
    const renderResponseText = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('*   ') || trimmedLine.startsWith('* ')) {
                const bulletText = trimmedLine.replace(/^\*\s+/, '');
                return (
                    <li key={i} className="response-bullet" dangerouslySetInnerHTML={{
                        __html: bulletText
                            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                    }} />
                );
            }
            if (!trimmedLine) return null;
            return (
                <p key={i} className="response-line" dangerouslySetInnerHTML={{
                    __html: trimmedLine
                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
                }} />
            );
        });
    };

    // Filter search results: remove test/copy entries, require valid https link, sort by relevance
    const isValidLink = (link) => typeof link === 'string' && (link.startsWith('https://') || link.includes('course-details'));

    const getFilteredResults = (results) => {
        if (!Array.isArray(results)) return [];
        return results
            .filter((r) => isValidLink(r.launchUrl) && r.relevance_score >= 0.4 && !/^(test|copy of)/i.test(r.title))
            .sort((a, b) => b.relevance_score - a.relevance_score);
    };

    // Get recommended courses from search_sbdr_courses (top scoring with links) for carousel
    const getRecommendedForCarousel = (results) => {
        if (!Array.isArray(results)) return [];
        return results
            .filter((r) => r.launchUrl && r.launchUrl.includes('course-details')&& !/^(test|copy of|\[draft\])/i.test(r.title))
            .sort((a, b) => b.relevance_score - a.relevance_score);
    };

    const enrichSources = (r) => {
        if (!r.link) return r;

        const { typeaheadCardsMapping, allTopicsData } = oneSearchHardCodeData;

        // If URL contains 'course-details' — delegate to enrichCourseResult
        if (r.link.includes('course-details')) {
            if(r.title && r.title.toLowerCase().includes('quick tip')) {
                return enrichCourseResult({ ...r, format: 'Quick Tip', id: r.link.split('/course-details/')[1]?.split('/')[0]})
            }
            return enrichCourseResult(r);
        }

        // If URL contains 'results' — look up topic in allTopicsData
        if (r.link.includes('results')) {
            const decodedLink = decodeURIComponent(r.link).toLowerCase();
            const topicMatch = allTopicsData.find((topic) => decodedLink.replace(/\s+/g, '').includes(topic.key));
            if (topicMatch) {
                return {
                    ...r,
                    title: topicMatch.label,
                    imageUrl: topicMatch.aiImgUrl || null,
                    format: 'Topic',
                };
            }
            return { ...r, format: 'Topic' };
        }

        // All other cases — look up in typeaheadCardsMapping
        // Sort keys longest-first so more specific keys (e.g. 'customerSupport/funding') match before shorter ones
        const mappingKeys = Object.keys(typeaheadCardsMapping).sort((a, b) => b.length - a.length);
        const matchedKey = mappingKeys.find((key) => r.link.toLowerCase().includes(key.toLowerCase()));
        if (matchedKey) {
            const mapped = typeaheadCardsMapping[matchedKey];
            return {
                ...r,
                title: mapped.label ?? r.title,
                imageUrl: mapped.aiImgUrl || null,
                format: mapped.eyebrowText,
            };
        }

        return r;
    }
    // Enrich a courseResult item with imageUrl, duration, and format from Redux state
    const enrichCourseResult = (r) => {
        const idFromLink = r.launchUrl
            ? (r.launchUrl.split('/course-details/')[1])?.split('/')[0]
            : r.link ? (r.link.split('/course-details/')[1])?.split('/')[0] : null;
        const courseMatch = idFromLink && allCourses.find((c) => c.id === idFromLink);
        if (courseMatch) {
            return { ...r, imageUrl: courseMatch.image, duration: courseMatch.duration, format: 'Course', type: courseMatch.type, icon: courseMatch.icon };
        }
        const eventMatch = idFromLink && allEventsWithTimeZone.find((e) => e.id === idFromLink);
        if (eventMatch) {
            return { ...r, imageUrl: eventMatch.image, duration: typeof eventMatch.duration === 'string' ? eventMatch.duration : eventMatch.duration?.time, format: 'Event', type: eventMatch.type, icon: eventMatch.icon };
        }
        const pastEventMatch = idFromLink && Array.isArray(allPastEvents) && allPastEvents.find((e) => e.id === idFromLink);
        if (pastEventMatch) {
            return { ...r, imageUrl: pastEventMatch.imageUrl, duration: typeof pastEventMatch.duration === 'string' ? pastEventMatch.duration : `${pastEventMatch.duration/60} minutes`, format: 'Event', type: pastEventMatch.type === 'course'? '' : pastEventMatch.type, icon: pastEventMatch.icon };
        }
        const qtMatch = r.id && combinedQuickTips.find((qt) => String(qt.courseId) === r.id);
        if (qtMatch) {
            return { ...r, imageUrl: r.imageUrl? r.imageUrl: qtMatch.desktopImage , duration: qtMatch.dateAndTimeValue ?? qtMatch.duration, format: 'Quick Tip', type: qtMatch.courseType, icon: qtMatch.icon, id: qtMatch.courseId };
        }
        const offerMatch = idFromLink && allOffers.find((o) => String(o.id) === idFromLink);
        if (offerMatch) {
            return { ...r, imageUrl: offerMatch.desktopImage ?? offerMatch.image, duration: offerMatch.duration?.time ?? offerMatch.duration, format: 'Offer', type: offerMatch.type, icon: offerMatch.icon, id: offerMatch.id };
        }
        return { ...r, format: 'Course', duration: `${r.duration/60}  minutes`, type: "" };
    };

    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const isTablet = typeof window !== 'undefined' && window.innerWidth > 767 && window.innerWidth <= 1024;

    return (
        <div className="one-search-container">
            {/* ── Search Input ── */}
            <div className="search-input-wrapper">
                <input
                    aria-label="Search"
                    name="Search"
                    type="text"
                    className="search-input"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                    disabled={isLoading}
                />
                <span className="icon-space">
                    {searchQuery !== '' && (
                        <ButtonIcon
                            kind="ghost"
                            size="large"
                            ariaLabel={t('Clear search')}
                            onClick={() => setSearchQuery('')}
                            renderIcon={(props) => (
                                <Icon name="close" size="large" color="#000000" {...props} />
                            )}
                        />
                    )}
                    <ButtonIcon
                        kind="ghost"
                        size="large"
                        ariaLabel={t(searchPlaceholder)}
                        onClick={() => handleSearch()}
                        disabled={isLoading}
                        renderIcon={(props) => (
                            <Icon name="search" size="large" color="#000000" {...props} />
                        )}
                    />
                </span>
            </div>

            {/* ── Search Results History ── */}
            <div className="os-history" ref={historyRef}>
                {searchHistory.map((entry, historyIndex) => (
                    <div key={historyIndex} className="os-entry">

                        {/* ── Query label ── */}
                        <p className="os-query-label">
                            {t('Results for')}: <strong>{entry.query}</strong>
                        </p>

                        {/* ── Loading skeleton ── */}
                        {entry.isStreaming && !entry.streamingText && (
                            <div className="os-generating">
                                <svg className="os-generating__icon" width="16" height="16" viewBox="0 0 62 68" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M33.4065 67.3684C31.8162 67.3684 30.3884 66.3418 29.6807 65.3235L29.5182 65.0622C28.8722 63.9718 28.6006 62.8979 28.4072 61.968C28.2776 61.4084 28.0842 60.5135 27.862 59.4766C27.3354 57.0284 26.6153 53.6771 26.0886 51.7761C25.887 51.0849 25.3995 50.192 24.7144 50.0439C23.9881 49.8793 22.8134 49.6283 21.4968 49.3444C19.4086 48.8959 16.9686 48.3713 15.4071 48.0092C13.8292 47.6656 12.1566 47.2398 11.0313 46.5526C10.7761 46.4169 10.5972 46.2646 10.4984 46.1803C9.47388 45.3285 8.92046 44.1435 8.92046 42.8824C8.92046 41.6213 9.47388 40.4342 10.4408 39.6298C10.5828 39.5105 10.772 39.35 11.0292 39.2122C12.1628 38.5168 13.8456 38.091 15.3536 37.768C16.9357 37.4018 19.3119 36.8895 21.3713 36.4472C22.7373 36.153 23.9635 35.8896 24.7 35.723C25.3995 35.5708 25.887 34.6779 26.0969 33.9578C26.6153 32.0857 27.3354 28.7343 27.862 26.2861C28.0842 25.2493 28.2776 24.3543 28.4155 23.7557C28.6006 22.8669 28.8722 21.7951 29.4174 20.8693L29.6663 20.4619C30.3864 19.423 31.8141 18.3943 33.4065 18.3943C35.0029 18.3943 36.4287 19.425 37.1343 20.4475L37.2927 20.7026C37.9449 21.8053 38.2144 22.8751 38.4057 23.7989C38.5374 24.3687 38.7349 25.2925 38.9653 26.3602C39.4879 28.7858 40.2017 32.1063 40.7222 33.9866C40.9239 34.6758 41.4135 35.5708 42.0965 35.7189C42.8495 35.8896 44.0818 36.155 45.454 36.4492C47.5093 36.8915 49.8772 37.4018 51.4058 37.7556C52.9611 38.091 54.644 38.5148 55.7796 39.2101C56.0306 39.3459 56.2096 39.4961 56.3084 39.5784C57.3391 40.4404 57.8925 41.6254 57.8925 42.8824C57.8925 44.1394 57.3391 45.3224 56.3763 46.1309C56.2261 46.2543 56.0368 46.4127 55.7838 46.5506C54.6584 47.2398 52.9858 47.6656 51.4881 47.9907C49.8464 48.3692 47.4126 48.8938 45.3285 49.3403C44.0057 49.6262 42.8248 49.8793 42.1089 50.0398C41.4135 50.192 40.9239 51.0869 40.714 51.807C40.2017 53.6565 39.4879 56.977 38.9653 59.4025C38.7349 60.4703 38.5374 61.394 38.3954 62.0091C38.2144 62.8897 37.9449 63.9615 37.3915 64.8955L37.1652 65.2741C36.4266 66.3398 35.0009 67.3684 33.4065 67.3684Z" fill="#808080"/>
                                </svg>
                                <div className="os-generating__text">
                                    <span className="vds-body-sm os-generating__label">{t('Generating response')}</span>
                                    <div className="os-skeleton-group">
                                        <div className="os-skeleton-bar" />
                                        <div className="os-skeleton-bar" />
                                        <div className="os-skeleton-bar" />
                                        <div className="os-skeleton-bar" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── AI Summary (streamed / final text) ── */}
                        {(entry.streamingText || entry.finalText) && (
                            <div
                                className={`os-ai-summary${entry.isStreaming ? ' streaming' : ''}`}
                                ref={(el) => { summaryRefs.current[historyIndex] = el; }}
                                tabIndex={-1}
                            >
                                <div className="os-ai-summary__content">
                                    {renderResponseText(entry.finalText || entry.streamingText)}
                                </div>
                            </div>
                        )}

                        {/* ── Error ── */}
                        {entry.error && (
                            <p className="os-error">{t(entry.error)}</p>
                        )}

                        {/* ── Sources Accordion (Citations) ── */}
                        {!entry.isStreaming && entry.citations?.length > 0 && (
                            <div className="os-sources">
                                <button
                                    type="button"
                                    className="os-sources__toggle"
                                    ref={(el) => { sourcesButtonRefs.current[historyIndex] = el; }}
                                    onClick={() => toggleSources(historyIndex)}
                                    aria-expanded={entry.sourcesOpen}
                                >
                                    <span className="vds-body-lg font-eDS-bold">{t('Sources')}</span>
                                    <svg
                                        className={`os-sources__chevron${entry.sourcesOpen ? ' open' : ''}`}
                                        width="20" height="20" viewBox="0 0 20 20" fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M2 5L10 14L18 5" stroke="#000" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                {entry.sourcesOpen && (
                                    <div className="os-sources__panel">
                                        {entry.citations
                                            .filter((c) => isValidLink(c.recordUrl))
                                            .map((c, ci) => {
                                                const enriched = enrichSources({ link: c.recordUrl, title: c.recordTitle });
                                                return (
                                                    <SourceCard
                                                        key={ci}
                                                        format={enriched.format}
                                                        title={enriched.title}
                                                        imageUrl={enriched.imageUrl}
                                                        link={enriched.format === 'Quick Tip' || enriched.format === 'Offer' ? `${window.location.origin}/course-details/${enriched.id}` : enriched.link}
                                                    />
                                                );
                                            })}
                                        {/* Also show high-relevance search results as additional sources */}
                                        {getFilteredResults(entry.searchResults)
                                            .filter((r) => !entry.citations.some((c) => c.recordUrl === r.launchUrl))
                                            .slice(0, 4)
                                            .map((r, ri) => {
                                                const enriched = enrichSources(r);
                                                return (
                                                    <SourceCard
                                                        key={`sr-${ri}`}
                                                        format={enriched.format}
                                                        title={enriched.title}
                                                        imageUrl={enriched.imageUrl}
                                                        link={enriched.link}
                                                    />
                                                );
                                            })
                                        }
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Recommended Courses Carousel ── */}
                        {!entry.isStreaming && getRecommendedForCarousel(entry.courseResults).length > 0 && (
                            <div className="os-recommended">
                                <div className="vds-title-sm mobile-vds-title-xs font-eDS-bold mb-2X">
                                    {t('Recommended courses')}
                                </div>
                                <Carousel
                                    layout={isMobile ? '1UP' : isTablet ? '2UP' : '3UP'}
                                    gutter={isMobile ? '12px' : '24px'}
                                    peek={isMobile ? 'standard' : 'none'}
                                    surface="light"
                                    paginationInset="12px"
                                    pagination={{
                                        kind: 'lowContrast',
                                        hideBorder: true,
                                        floating: true,
                                    }}
                                    paginationDisplay="onHover"
                                    aspectRatio="none"
                                    data={getRecommendedForCarousel(entry.courseResults).map((r) => {
                                        const enriched = enrichCourseResult(r);
                                        return {
                                            title: { size: 'titleLarge', primitive: 'h3', children: enriched.title },
                                            backgroundColor: 'white',
                                            showBorder: true,
                                            onClick: () => { window.open(enriched.link); },
                                            _custom: enriched,
                                        };
                                    })}
                                    renderItem={(props) => (
                                        <RecommendedCourseCard
                                            title={props._custom?.title || props.title?.children}
                                            snippet={props._custom?.snippet}
                                            link={props._custom?.link}
                                            format={props._custom?.format}
                                            duration={props._custom?.duration}
                                            imageUrl={props._custom?.imageUrl}
                                            icon={props._custom?.icon}
                                            type={props._custom?.type}
                                        />
                                    )}
                                />
                            </div>
                        )}

                        {/* ── Follow-up Question CTAs ── */}
                        {!entry.isStreaming && entry.followUpQuestions?.length > 0 && (
                            <div className="os-followups">
                                <p className="vds-title-xs mobile-vds-title-2xs os-followups__label">
                                    {entry.followUpQuestions.length > 0 && t('Would you like to focus on strategies for attracting new customers or building long-term loyalty?')}
                                </p>
                                <div className="os-followups__chips">
                                    {entry.followUpQuestions.map((q, qi) => (
                                        <button
                                            key={qi}
                                            className="os-followups__chip"
                                            onClick={() => handleSearch(q)}
                                        >
                                            <span className="vds-body-lg font-eDS-bold mobile-vds-body-sm mobile-font-eTX-bold">
                                                {q}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Feedback row ── */}
                        {!entry.isStreaming && (entry.finalText || entry.streamingText) && (
                            <div className="os-feedback">
                                <span className="vds-body-small">
                                    {t('Generated with AI. This information is for educational purposes only. Verizon does not provide tax or legal advice. Please consult with a professional.')}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
